/** VUEX 'thin' store for new custom area manager module + area pickers  */

/** helper functions */

//convert value (number, array, object) to array of ids
const extractIdsToArray = (value) => {
  const arr = Array.isArray(value) ? value : [value]; //string or object to array
  return arr.map((item) => (typeof item === "object" ? item["id"] : item)); //extract object id key or return string
};

//normalise string for searching
export const normaliseString = (str) => {
  return (str || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
};

/** VUEX Definition */
export const customAreaManager = {
  namespaced: true,
  /** state (data) */
  state: () => ({
    areaDataLevelTimepoints: [], // available timepoints for area data levels
    areaDataLevels: [], // area data level data (LA, LSOA etc)
    selectedAreaDataLevelId: null, // selected data level
    selectedDataLevelTimepoint: null,
    selectedAreaIds: [], //ids of any selected areas
    standardAreas: {}, // data for standard areas that have been fetched for each data level
    standardAreaParents: {}, // area level 6 (e.g. LA) parents for areas (used to filter LSOA, MSOA, wards)
    customAreaCategories: [], //existing custom area categories
    selectedCategoryId: null, //selected custom area category
    customAreaName: "", //name for new single custom area
    customAreaNameOverrides: {}, //name overrides for multiple custom areas { areaId: name, ... }
    customAreaDescription: "", //description for new custom area
    singleCustomArea: true, //combine selected areas into single custom area
    fetchingAreaDataLevels: false,
    fetchingTimepoints: false,
    fetchingAreas: false,
    savingCustomAreas: false,
    formErrors: {}, //errorbag object from backend validation to trigger field error messages in component forms
    errorAreaIds: [], //area ids of multiple custom area save errors ['1830991',...]
    errorAreaReasons: {}, //reason area save errored {'1830991': 'duplicate name'}
  }),
  /** setters */
  mutations: {
    setAreaDataLevelTimepoints(state, value) {
      state.areaDataLevelTimepoints = value;
    },
    setAreaDataLevels(state, value) {
      state.areaDataLevels = value;
    },
    setSelectedAreaDataLevelId(state, value) {
      state.selectedAreaDataLevelId = value;
    },
    setSelectedDataLevelTimepoint(state, value) {
      state.selectedDataLevelTimepoint = value;
    },
    addSelectedAreaIds(state, value) {
      //fast batch add using set for large dataset
      const ids = extractIdsToArray(value);
      const set = new Set(state.selectedAreaIds);
      ids.forEach((id) => set.add(id));
      state.selectedAreaIds = Array.from(set);
    },
    removeSelectedAreaIds(state, value) {
      //fast batch using set for large dataset
      const ids = extractIdsToArray(value);
      const set = new Set(ids.map((v) => String(v))); //set of string ids
      state.selectedAreaIds = state.selectedAreaIds.filter(
        (id) => !set.has(String(id)),
      );
    },
    clearSelectedAreaIds(state) {
      state.selectedAreaIds = [];
    },
    setStandardAreas(state, { dataLevelId, timepoint, value }) {
      if (!state.standardAreas[dataLevelId]) {
        state.standardAreas[dataLevelId] = {};
      }
      state.standardAreas[dataLevelId][timepoint] = value;
    },
    setStandardAreaParents(state, { timepoint, value }) {
      state.standardAreaParents[timepoint] = value;
    },
    setFetchingAreaDataLevels(state, value) {
      state.fetchingAreaDataLevels = value;
    },
    setFetchingTimepoints(state, value) {
      state.fetchingTimepoints = value;
    },
    setFetchingAreas(state, value) {
      state.fetchingAreas = value;
    },
    setCustomAreaCategories(state, value) {
      state.customAreaCategories = value;
    },
    setSelectedCategoryId(state, value) {
      state.selectedCategoryId = value;
    },
    setCustomAreaName(state, value) {
      state.customAreaName = value;
    },
    setCustomAreaDescription(state, value) {
      state.customAreaDescription = value;
    },
    setSingleCustomArea(state, value) {
      state.singleCustomArea = value;
    },
    addCustomAreaNameOverride(state, { areaId, name }) {
      // Only one entry per areaId will exist, as object keys are unique
      state.customAreaNameOverrides = {
        ...state.customAreaNameOverrides,
        [areaId]: name,
      };
    },
    removeCustomAreaNameOverride(state, areaId) {
      const id = String(areaId);
      const rest = Object.fromEntries(
        Object.entries(state.customAreaNameOverrides).filter(
          ([key]) => key !== id,
        ),
      );
      state.customAreaNameOverrides = rest;
    },
    clearAllCustomAreaNameOverrides(state) {
      state.customAreaNameOverrides = {};
    },
    setSavingCustomAreas(state, value) {
      state.savingCustomAreas = value;
    },
    setFormErrors(state, value) {
      state.formErrors = value;
    },
    setErrorAreaIds(state, ids) {
      state.errorAreaIds = ids;
    },
    setErrorAreaReasons(state, errors) {
      //normalise key to string
      const reasons = {};
      if (errors && typeof errors === "object") {
        for (const [k, v] of Object.entries(errors)) {
          reasons[String(k)] = v;
        }
      }

      state.errorAreaReasons = reasons;
    },
    clearErrorAreas(state) {
      state.errorAreaIds = [];
      state.errorAreasReasons = {};
    },
  },
  /** getters */
  getters: {
    /* getters should be derived values....bit like computed properties from state */
    /** area data levels or timepoints are being fetched */
    areaDataLevelsLoading: (state) =>
      state.fetchingAreaDataLevels || state.fetchingTimepoints,
    /** return the latest year for selected data level */
    latestTimepointForAreaLevel: (state) => {
      if (state.selectedAreaDataLevelId == null) return null;

      const levelTimepointData = state.areaDataLevelTimepoints.find(
        (t) => t.area_level === state.selectedAreaDataLevelId,
      );
      //get years entry (fetching only numeric values)
      const years = levelTimepointData.years.filter(
        (y) => typeof y === "number",
      );

      if (!years || years.length === 0) return null;

      return Math.max(...years);
    },
    /** return selected data level title */
    selectedDataLevelName: (state) => {
      const areaLevelData = state.areaDataLevels.find(
        (l) => l.area_level === state.selectedAreaDataLevelId,
      );

      if (areaLevelData == null) return null;

      return areaLevelData["area_level_name"];
    },
    /** return all timepoints for selected data level */
    selectedDataLevelTimepoints: (state) => {
      const timepointData = state.areaDataLevelTimepoints.find(
        (t) => t.area_level === state.selectedAreaDataLevelId,
      );

      if (timepointData == null) return null;

      return timepointData["years"];
    },
    /**
     * return area data for selected datalevel and timepoint if we have it stored
     * provide normalised parent_name and area_name for faster searching
     */
    areaDataForLevelTimepoint: (state) => {
      const levelData = state.standardAreas?.[state.selectedAreaDataLevelId];
      if (!levelData) return null;

      const data = levelData?.[state.selectedDataLevelTimepoint];
      if (!data) return null;

      // Detect shape by checking the first item
      const isGrouped = Array.isArray(data?.[0]?.areas);

      if (isGrouped) {
        // [{ parent_id, parent_name, areas: [...] }]
        return data.map((p) => ({
          ...p,
          _pname_normalised: normaliseString(p.parent_name),
          areas: Array.isArray(p.areas)
            ? p.areas.map((a) => ({
                ...a,
                _aname_normalised: normaliseString(a.area_name),
              }))
            : [],
        }));
      }

      // Flat: [{ id, area_name, ... }]
      return data.map((a) => ({
        ...a,
        _aname_normalised: normaliseString(a.area_name),
      }));
    },
    /** return area level 6 parents for datalevel and timepoint if we have it stored */
    parentsForAreaLevelTimepoint: (state) => {
      if (!state.standardAreaParents?.[state.selectedDataLevelTimepoint]) {
        return null;
      }

      return state.standardAreaParents[state.selectedDataLevelTimepoint];
    },
    /** custom area category details for given id */
    customAreaCategoryById: (state) => (id) => {
      return state.customAreaCategories.find((c) => c.id === id);
    },
    // loops through all areas (data levels and then timepoints) and returns the onces that are selected.
    // skip duplicate area codes.
    selectedAreaData: (state) => {
      const selectedAreas = [];
      const seenAreaCodes = new Set();

      // Loop through all data levels in standardAreas
      for (const dataLevelId in state.standardAreas) {
        const levelData = state.standardAreas[dataLevelId];

        // Loop through all timepoints for this data level
        for (const timepoint in levelData) {
          const data = levelData[timepoint];
          if (!data) continue;

          // Check if data is grouped (parent/child structure)
          const isGrouped = Array.isArray(data?.[0]?.areas);

          if (isGrouped) {
            // Handle grouped data: [{ parent_id, parent_name, areas: [...] }, ...]
            data.forEach((parent) => {
              if (Array.isArray(parent.areas)) {
                parent.areas.forEach((area) => {
                  if (
                    state.selectedAreaIds.includes(area.id) &&
                    !seenAreaCodes.has(area.area_code)
                  ) {
                    selectedAreas.push(area);
                    seenAreaCodes.add(area.area_code);
                  }
                });
              }
            });
          } else {
            // Handle flat data: [{ id, area_name, ... }]
            data.forEach((area) => {
              if (
                state.selectedAreaIds.includes(area.id) &&
                !seenAreaCodes.has(area.area_code)
              ) {
                selectedAreas.push(area);
                seenAreaCodes.add(area.area_code);
              }
            });
          }
        }
      }

      return selectedAreas;
    },
    /**  return backend validation reason for failure to save an area in multiple areas */
    errorReasonForId: (state) => (id) =>
      state.errorAreaReasons[String(id)] || "",
  },
  /** actions */
  actions: {},
};
